import React from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { AuthContext } from '../../../../authContext/context';
import { useContext, useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../../lib/axiosIntercepter';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import BreadcrumbsWrapper from '../../../../components/breadcrumbs';

// Icons for elements
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SmartButtonIcon from '@mui/icons-material/SmartButton';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ImageIcon from '@mui/icons-material/Image';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CodeIcon from '@mui/icons-material/Code';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

// Code Import Modal Component
const CodeModal = ({ open, onClose, onImport, initialContent }) => {
  const [codeValue, setCodeValue] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  // Load initial content when modal opens
  useEffect(() => {
    if (open && initialContent) {
      setCodeValue(initialContent);
    }
  }, [open, initialContent]);

  const handleImport = () => {
    // Prevent double import
    if (isImporting) {
      console.log('⚠️ Import already in progress, ignoring duplicate');
      return;
    }

    if (codeValue.trim()) {
      setIsImporting(true);
      onImport(codeValue);
      setCodeValue('');
      onClose();
      // Reset after a short delay
      setTimeout(() => setIsImporting(false), 500);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#434759',
          color: 'white'
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#434759',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Import template</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ backgroundColor: '#434759', p: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1, fontSize: '12px' }}>
            Paste your complete HTML code below. The code will be used exactly as provided:
          </Typography>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={12}
          value={codeValue}
          onChange={(e) => setCodeValue(e.target.value)}
          placeholder='Paste your HTML code here...'
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              color: 'white',
              fontFamily: 'monospace',
              fontSize: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
            },
            '& .MuiInputBase-input': {
              color: 'white !important',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          }}
        />

        {/* Line numbers overlay (optional) */}
        <Box sx={{
          position: 'absolute',
          top: '140px',
          left: '35px',
          fontSize: '12px',
          fontFamily: 'monospace',
          color: 'rgba(255,255,255,0.5)',
          pointerEvents: 'none',
          lineHeight: '18px'
        }}>
          {codeValue.split('\n').map((_, index) => (
            <div key={index} style={{ height: '18px' }}>
              {index + 1}
            </div>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: '#434759', p: 3, pt: 1 }}>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!codeValue.trim() || isImporting}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.3)'
            },
            '&:disabled': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)'
            }
          }}
        >
          {isImporting ? 'Importing...' : 'Import Code'}
        </Button>
        <Button
          onClick={onClose}
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Draggable Elements Panel
const ElementsPanel = ({ onAddElement, onOpenCodeModal }) => {
  const elements = [
    { id: 'section', name: '1 Section', icon: ViewColumnIcon, type: 'section', columns: 1 },
    { id: 'section-2', name: '1/2 Section', icon: ViewColumnIcon, type: 'section', columns: 2 },
    { id: 'section-3', name: '1/3 Section', icon: ViewColumnIcon, type: 'section', columns: 3 },
    { id: 'section-mixed', name: '2/3 Section', icon: ViewColumnIcon, type: 'section', columns: 'mixed' },
    { id: 'text', name: 'Text', icon: TextFieldsIcon, type: 'text' },
    { id: 'button', name: 'Button', icon: SmartButtonIcon, type: 'button' },
    { id: 'divider', name: 'Divider', icon: HorizontalRuleIcon, type: 'divider' },
    { id: 'image', name: 'Image', icon: ImageIcon, type: 'image' },
    { id: 'quote', name: 'Quote', icon: FormatQuoteIcon, type: 'quote' }
  ];

  return (
    <Box 
      sx={{ 
        backgroundColor: '#434759',
        minHeight: '100vh',
        p: 2,
        position: 'fixed',
        right: 0,
        top: 0,
        width: '300px',
        overflowY: 'auto',
        zIndex: 1000
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 3, textAlign: 'center' }}>
        Elements
      </Typography>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
        {elements.map((element) => {
          const IconComponent = element.icon;
          return (
            <Box
              key={element.id}
              sx={{
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.2)',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }}
              onClick={() => onAddElement(element)}
            >
              <IconComponent sx={{ fontSize: 30, mb: 1 }} />
              <Typography variant="caption" display="block" sx={{ fontSize: '12px', fontWeight: 500 }}>
                {element.name}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Code Export/Import Button */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 3 }}>
        <Button
          fullWidth
          onClick={onOpenCodeModal}
          sx={{
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: 'white',
            border: '2px solid rgba(255,255,255,0.2)',
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }
          }}
          startIcon={<CodeIcon sx={{ fontSize: 24 }} />}
        >
          Export / Import Code
        </Button>
      </Box>
    </Box>
  );
};

// Template Element Component
const TemplateElement = ({ element, index, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const authCtx = useContext(AuthContext);

  const handleUpdate = (field, value) => {
    console.log(`Updating field "${field}" with value:`, value);
    console.log('Current element before update:', element);
    const updatedElement = { ...element, [field]: value };
    console.log('Updated element:', updatedElement);
    onUpdate(index, updatedElement);
  };

  const renderElement = () => {
    switch (element.type) {
      case 'section':
        return (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              mb: 2, 
              border: '2px dashed #ddd',
              minHeight: '100px',
              position: 'relative'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {element.columns === 1 ? '1 Column Section' : 
                 element.columns === 2 ? '2 Column Section' : 
                 element.columns === 3 ? '3 Column Section' : '2/3 Column Section'}
              </Typography>
              <Box>
                <IconButton size="small" sx={{ color: '#999' }}>
                  <DragIndicatorIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#f44336' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 
                element.columns === 1 ? '1fr' :
                element.columns === 2 ? '1fr 1fr' :
                element.columns === 3 ? '1fr 1fr 1fr' : '2fr 1fr',
              gap: 2,
              minHeight: '60px'
            }}>
              {Array.from({ length: element.columns === 'mixed' ? 2 : element.columns }).map((_, colIndex) => (
                <Box 
                  key={colIndex}
                  sx={{ 
                    border: '1px dashed #ccc', 
                    p: 1, 
                    borderRadius: 1,
                    backgroundColor: '#f9f9f9'
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Drop elements here
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        );
        
      case 'text':
        return (
          <Box 
            sx={{ 
              p: 2, 
              mb: 2, 
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              backgroundColor: 'white',
              position: 'relative',
              '&:hover': {
                borderColor: '#667eea',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 600 }}>
                Text Block
              </Typography>
              <Box>
                <IconButton size="small" sx={{ color: '#999' }}>
                  <DragIndicatorIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#f44336' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            {editing ? (
              <TextField
                fullWidth
                multiline
                rows={3}
                value={element.content || 'Enter your text here...'}
                onChange={(e) => handleUpdate('content', e.target.value)}
                onBlur={() => setEditing(false)}
                autoFocus
                variant="outlined"
              />
            ) : (
              <Box
                sx={{
                  cursor: 'pointer',
                  minHeight: '60px',
                  p: 2,
                  backgroundColor: '#f8f9fa',
                  border: '1px dashed #ddd',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
                onClick={() => setEditing(true)}
              >
                {element.content ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: element.content }}
                    style={{ color: '#333', width: '100%' }}
                  />
                ) : (
                  <Typography sx={{ color: '#999' }}>
                    Click to add text content...
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
        
      case 'button':
        return (
          <Paper elevation={1} sx={{ p: 2, mb: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Button</Typography>
              <Box>
                <IconButton size="small" sx={{ color: '#999' }}>
                  <DragIndicatorIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#f44336' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  px: 3,
                  py: 1.5
                }}
              >
                {element.text || 'Click Here'}
              </Button>
              
              {editing && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    size="small"
                    label="Button Text"
                    value={element.text || ''}
                    onChange={(e) => handleUpdate('text', e.target.value)}
                    sx={{ mr: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Link URL"
                    value={element.url || ''}
                    onChange={(e) => handleUpdate('url', e.target.value)}
                  />
                  <Button size="small" onClick={() => setEditing(false)} sx={{ ml: 1 }}>
                    Done
                  </Button>
                </Box>
              )}
              
              {!editing && (
                <Button size="small" onClick={() => setEditing(true)} sx={{ mt: 1 }}>
                  Edit Button
                </Button>
              )}
            </Box>
          </Paper>
        );
        
      case 'divider':
        return (
          <Paper elevation={1} sx={{ p: 2, mb: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Divider</Typography>
              <Box>
                <IconButton size="small" sx={{ color: '#999' }}>
                  <DragIndicatorIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#f44336' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ 
              height: '2px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '1px'
            }} />
          </Paper>
        );
        
      case 'image':
        return (
          <Paper elevation={1} sx={{ p: 2, mb: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Image</Typography>
              <Box>
                <IconButton size="small" sx={{ color: '#999' }}>
                  <DragIndicatorIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#f44336' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ 
              textAlign: 'center', 
              border: '2px dashed #ddd', 
              p: 4, 
              borderRadius: 1,
              backgroundColor: '#f9f9f9',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {element.src ? (
                <Box sx={{ 
                  position: 'relative',
                  display: 'inline-block',
                  maxWidth: '100%',
                  '& img': {
                    maxWidth: '100%',
                    maxHeight: '400px',
                    height: 'auto',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'block'
                  }
                }}>
                  <img 
                    key={`img-${element.src}-${element._key || ''}`}
                    src={element.src} 
                    alt={element.alt || 'Email image'} 
                    onLoad={() => console.log('✅ Image loaded successfully!')}
                    onError={(e) => {
                      console.error('❌ Image failed to load:', element.src);
                      console.error('Error:', e);
                    }}
                    style={{ display: 'block', maxWidth: '100%' }}
                  />
                </Box>
              ) : (
                <>
                  <ImageIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography color="text.secondary">Click to add image</Typography>
                </>
              )}
              
              {(!element.src || editing) && (
                <Box sx={{ mt: 2 }}>
                  {/* Upload Image Option */}
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id={`image-upload-${index}`}
                      type="file"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Check file size (limit to 5MB)
                          const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                          if (file.size > maxSize) {
                            Swal.fire({
                              icon: 'error',
                              title: 'File too large',
                              text: 'Please select an image smaller than 5MB'
                            });
                            return;
                          }
                          
                          // Check file type
                          const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                          if (!validTypes.includes(file.type)) {
                            Swal.fire({
                              icon: 'error',
                              title: 'Invalid file type',
                              text: 'Please select a valid image file (JPEG, PNG, GIF, or WebP)'
                            });
                            return;
                          }
                          
                          // Upload image to server
                          const formData = new FormData();
                          formData.append('image', file);
                          
                          try {
                            const response = await fetch('/api/v1/admin/private/email-templates/upload-image', {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${authCtx.token}`
                              },
                              body: formData
                            });
                            
                            const data = await response.json();
                            
                            if (response.ok && data.status === 200) {
                              // Use the server URL instead of base64
                              handleUpdate('src', data.data.url);
                              Swal.fire({
                                icon: 'success',
                                title: 'Success',
                                text: 'Image uploaded successfully!'
                              });
                            } else {
                              throw new Error(data.message || 'Failed to upload image');
                            }
                          } catch (error) {
                            console.error('Image upload error:', error);
                            Swal.fire({
                              icon: 'error',
                              title: 'Upload failed',
                              text: error.message || 'Failed to upload image. Please try again.'
                            });
                          }
                        }
                      }}
                    />
                    <label htmlFor={`image-upload-${index}`}>
                      <Button
                        variant="contained"
                        component="span"
                        size="small"
                        fullWidth
                        sx={{ 
                          mb: 1,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        }}
                        startIcon={<ImageIcon />}
                      >
                        Upload Image
                      </Button>
                    </label>
                  </Box>
                  
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    OR
                  </Typography>
                  
                  <TextField
                    size="small"
                    label="Image URL"
                    fullWidth
                    value={element.src || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Check if it's a base64 image
                      if (value.startsWith('data:image')) {
                        Swal.fire({
                          icon: 'error',
                          title: 'Invalid URL',
                          text: 'Base64 images are not allowed. Please use the Upload Image button instead.'
                        });
                        return;
                      }
                      handleUpdate('src', value);
                    }}
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    size="small"
                    label="Alt Text"
                    fullWidth
                    value={element.alt || ''}
                    onChange={(e) => handleUpdate('alt', e.target.value)}
                  />
                  <Button size="small" onClick={() => setEditing(false)} sx={{ mt: 1 }}>
                    Done
                  </Button>
                </Box>
              )}
              
              {!editing && element.src && (
                <Button size="small" onClick={() => setEditing(true)} sx={{ mt: 1 }}>
                  Edit Image
                </Button>
              )}
            </Box>
          </Paper>
        );
        
      case 'quote':
        return (
          <Paper elevation={1} sx={{ p: 2, mb: 2, position: 'relative' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Quote</Typography>
              <Box>
                <IconButton size="small" sx={{ color: '#999' }}>
                  <DragIndicatorIcon />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(index)} sx={{ color: '#f44336' }}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
            
            <Box sx={{ 
              borderLeft: '4px solid #667eea', 
              pl: 2, 
              backgroundColor: '#f8f9ff',
              p: 2,
              borderRadius: '0 4px 4px 0'
            }}>
              {editing ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={element.content || 'Enter your quote here...'}
                  onChange={(e) => handleUpdate('content', e.target.value)}
                  onBlur={() => setEditing(false)}
                  autoFocus
                  variant="standard"
                />
              ) : (
                <div
                  style={{ cursor: 'pointer', fontStyle: 'italic', width: '100%' }}
                  onClick={() => setEditing(true)}
                >
                  {element.content ? (
                    <div dangerouslySetInnerHTML={{ __html: `"${element.content}"` }} />
                  ) : (
                    <Typography sx={{ fontStyle: 'italic', color: '#999' }}>
                      "Enter your quote here..."
                    </Typography>
                  )}
                </div>
              )}
            </Box>
          </Paper>
        );
        
      default:
        return null;
    }
  };

  return renderElement();
};

export default function EditAdvanced() {
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();

  // Add ref to prevent double submission in StrictMode
  const isSubmittingRef = useRef(false);

  // Template form data
  const [templateData, setTemplateData] = useState({
    name: '',
    emailThemeId: '',
    productId: '',
    subject: '',
    elements: [],
    type: 'Advanced'
  });

  // Dropdown options
  const [dropdownOptions, setDropdownOptions] = useState({
    emailThemes: [],
    products: []
  });

  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const response = await axiosInstance.get('private/email-templates-options');
        const data = response.data;

        if (data.status === 200) {
          setDropdownOptions(data.data);

          // Set default email theme for advanced templates if not already set
          if (data.data.emailThemes && data.data.emailThemes.length > 0 && !templateData.emailThemeId) {
            setTemplateData(prev => ({
              ...prev,
              emailThemeId: prev.emailThemeId || data.data.emailThemes[0]._id
            }));
          }
        } else {
          throw new Error(data.message || 'Failed to fetch dropdown options');
        }
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load dropdown options'
        });
      } finally {
        setLoadingOptions(false);
      }
    };

    if (authCtx.token) {
      fetchDropdownOptions();
    }
  }, [authCtx.token]);

  // Fetch template data for editing
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await axiosInstance.get(`private/email-templates/${id}`);
        const data = response.data;

        if (data.status === 200) {
          const template = data.data;

          // Parse elements if they exist
          let parsedElements = [];
          if (template.elements) {
            try {
              parsedElements = JSON.parse(template.elements);
            } catch (e) {
              console.error('Error parsing elements:', e);
            }
          }

          setTemplateData({
            name: template.name || '',
            emailThemeId: template.emailThemeId?._id || '',
            productId: template.productId?._id || '',
            subject: template.subject || '',
            elements: parsedElements,
            type: 'Advanced'
          });
        } else {
          throw new Error(data.message || 'Failed to fetch template');
        }
      } catch (error) {
        console.error('Error fetching template:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error Loading Template',
          text: error.message || 'Failed to load template data'
        }).then(() => {
          navigate('/emailtemplates');
        });
      } finally {
        setLoadingTemplate(false);
      }
    };

    if (id && authCtx.token) {
      fetchTemplate();
    }
  }, [id, authCtx.token, navigate]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setTemplateData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add element to template
  const handleAddElement = (elementType) => {
    const newElement = {
      id: Date.now(),
      type: elementType.type,
      columns: elementType.columns,
      content: '',
      text: elementType.type === 'button' ? 'Click Here' : '',
      url: '',
      src: '',
      alt: ''
    };

    setTemplateData(prev => ({
      ...prev,
      elements: [...prev.elements, newElement]
    }));
  };

  // Update element
  const handleUpdateElement = (index, updatedElement) => {
    setTemplateData(prev => ({
      ...prev,
      elements: prev.elements.map((el, i) => i === index ? updatedElement : el)
    }));
  };

  // Delete element
  const handleDeleteElement = (index) => {
    setTemplateData(prev => ({
      ...prev,
      elements: prev.elements.filter((_, i) => i !== index)
    }));
  };

  // Generate complete HTML preview with full email template structure
  const generatePreviewHTML = () => {
    // Get theme details
    const selectedTheme = dropdownOptions.emailThemes.find(t => t._id === templateData.emailThemeId);
    const headerColor = selectedTheme?.headerColor || '#667eea';
    const backgroundColor = selectedTheme?.backgroundColor || '#f4f4f4';
    const themeName = selectedTheme?.name || 'Default Theme';

    // Generate body content from elements
    let bodyContent = '';
    templateData.elements.forEach((element) => {
      switch (element.type) {
        case 'text':
          bodyContent += `<div style="margin: 16px 0; font-family: Arial, sans-serif; line-height: 1.6; color: #333;">${element.content || 'Enter your text here...'}</div>`;
          break;
        case 'button':
          bodyContent += `<div style="text-align: center; margin: 24px 0;"><a href="${element.url || '#'}" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color:white;; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; font-family: Arial, sans-serif; font-weight: 500;">${element.text || 'Click Here'}</a></div>`;
          break;
        case 'divider':
          bodyContent += `<hr style="height: 2px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; margin: 24px 0; border-radius: 1px;" />`;
          break;
        case 'image':
          if (element.src) {
            bodyContent += `<div style="text-align: center; margin: 24px 0;"><img src="${element.src}" alt="${element.alt || ''}" style="max-width: 100%; height: auto; border-radius: 4px;" /></div>`;
          }
          break;
        case 'quote':
          bodyContent += `<blockquote style="border-left: 4px solid #667eea; padding-left: 16px; background-color: #f8f9ff; padding: 16px; margin: 24px 0; border-radius: 0 4px 4px 0; font-style: italic; font-family: Arial, sans-serif; color: #555;">"${element.content || 'Enter your quote here...'}"</blockquote>`;
          break;
        case 'section':
          bodyContent += `<div style="margin: 24px 0; padding: 16px; border: 2px dashed #ddd; border-radius: 8px; background-color: #f9f9f9; text-align: center; font-family: Arial, sans-serif; color: #666;">Section with ${element.columns} columns</div>`;
          break;
        default:
          break;
      }
    });

    // Return complete email HTML
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${templateData.subject || 'Email Template'}</title>
      </head>
      <body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: ${backgroundColor};">
        <table width="100%" cellpadding="0" cellspacing="0" style="background-color: ${backgroundColor}; padding: 40px 0;">
          <tr>
            <td align="center">
              <table width="600" cellpadding="0" cellspacing="0" style="background-color: color:white;; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                <!-- Header -->
                <tr>
                  <td style="background-color: ${headerColor}; padding: 30px; text-align: center;">
                    <h1 style="margin: 0; color: color:white;; font-size: 28px; font-weight: 600;">${templateData.name || 'Email Template'}</h1>
                    ${templateData.subject ? `<p style="margin: 10px 0 0 0; color: color:white;; font-size: 14px; opacity: 0.9;">${templateData.subject}</p>` : ''}
                  </td>
                </tr>
                <!-- Body -->
                <tr>
                  <td style="padding: 30px;">
                    ${bodyContent || '<p style="text-align: center; color: #999;">No content added yet</p>'}
                  </td>
                </tr>
                <!-- Footer -->
                <tr>
                  <td style="background-color: #f8f9fa; padding: 20px 30px; text-align: center; border-top: 1px solid #e0e0e0;">
                    <p style="margin: 0; font-size: 14px; color: #666;">© ${new Date().getFullYear()} {{company_name}}. All rights reserved.</p>
                    <p style="margin: 10px 0 0 0; font-size: 12px; color: #999;">
                      {{company_address}}<br>
                      <a href="{{unsubscribe_link}}" style="color: ${headerColor}; text-decoration: none;">Unsubscribe</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </body>
      </html>
    `;
  };

  // Handle code modal
  const handleOpenCodeModal = () => {
    setCodeModalOpen(true);
  };

  const handleCloseCodeModal = () => {
    setCodeModalOpen(false);
  };

  const handleImportCode = (htmlCode) => {
    // Store complete HTML as a single text element
    // This preserves the entire HTML structure and styling
    const newElements = [{
      id: Date.now(),
      type: 'text',
      content: htmlCode // Store complete HTML code
    }];

    setTemplateData(prev => ({
      ...prev,
      elements: newElements
    }));
  };

  // Generate HTML content from elements
  const generateHTMLContent = () => {
    let htmlContent = '';

    templateData.elements.forEach(element => {
      switch (element.type) {
        case 'text':
          // Check if content is imported complete HTML (starts with <!DOCTYPE or <html or <HTML)
          const trimmedContent = (element.content || '').trim();
          const isCompleteHTML = trimmedContent.toLowerCase().startsWith('<!doctype') ||
                                 trimmedContent.toLowerCase().startsWith('<html');

          if (isCompleteHTML) {
            // Use imported HTML exactly as-is
            htmlContent += element.content;
          } else {
            // Regular text content, wrap in <p> tag
            htmlContent += `<p>${element.content || 'Enter your text here...'}</p>\n`;
          }
          break;
        case 'button':
          htmlContent += `<div style="text-align: center; margin: 20px 0;"><a href="${element.url || '#'}" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color:white;; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block;">${element.text || 'Click Here'}</a></div>\n`;
          break;
        case 'divider':
          htmlContent += `<div style="height: 2px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); margin: 20px 0; border-radius: 1px;"></div>\n`;
          break;
        case 'image':
          if (element.src) {
            htmlContent += `<div style="text-align: center; margin: 20px 0;"><img src="${element.src}" alt="${element.alt || ''}" style="max-width: 100%; height: auto;" /></div>\n`;
          }
          break;
        case 'quote':
          htmlContent += `<blockquote style="border-left: 4px solid #667eea; padding-left: 16px; background-color: #f8f9ff; padding: 16px; margin: 20px 0; border-radius: 0 4px 4px 0; font-style: italic;">"${element.content || 'Enter your quote here...'}"</blockquote>\n`;
          break;
        case 'section':
          htmlContent += `<!-- ${element.columns === 1 ? '1' : element.columns === 2 ? '2' : element.columns === 3 ? '3' : '2/3'} Column Section -->\n<div style="margin: 20px 0;">Section with ${element.columns} columns</div>\n`;
          break;
        default:
          break;
      }
    });

    return htmlContent;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmittingRef.current) {
      console.log('⚠️ Submission already in progress, ignoring duplicate');
      return;
    }

    // Validation
    if (!templateData.name || !templateData.productId || 
        !templateData.subject) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    if (templateData.elements.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Template Empty',
        text: 'Please add at least one element to your template'
      });
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      const htmlContent = generateHTMLContent();
      
      const submitData = {
        ...templateData,
        content: htmlContent,
        isAdvanced: true,
        elements: JSON.stringify(templateData.elements)
      };

      const response = await axiosInstance.put(`private/email-templates/${id}`, submitData);
      const data = response.data;

      if (data.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Email template updated successfully!'
        }).then(() => {
          navigate('/emailtemplates');
        });
      } else {
        throw new Error(data.message || 'Failed to update template');
      }
    } catch (error) {
      console.error('Update template error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update email template'
      });
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  if (loadingOptions || loadingTemplate) {
    return (
      <div className='page-header pb-3'>
        <h1 className='mt-0 mb-0'>Edit Email Template</h1>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  // Get existing HTML content from elements
  const getExistingContent = () => {
    if (templateData.elements && templateData.elements.length > 0) {
      const textElement = templateData.elements.find(el => el.type === 'text');
      return textElement?.content || '';
    }
    return '';
  };

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Code Modal */}
      <CodeModal
        open={codeModalOpen}
        onClose={handleCloseCodeModal}
        onImport={handleImportCode}
        initialContent={getExistingContent()}
      />

      {/* Elements Panel - Hidden, use Code Modal instead */}
      <Box sx={{ display: 'none' }}>
        <ElementsPanel
          onAddElement={handleAddElement}
          onOpenCodeModal={handleOpenCodeModal}
        />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ p: 3 }}>
        <BreadcrumbsWrapper
          items={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Email Templates', path: '/emailtemplates' },
            { label: 'Edit Advanced Template', path: '' }
          ]}
        />
        <br />
        <div className='page-header pb-3'>
          <h1 className='mt-0 mb-0'>Edit Email Template</h1>
          <Button
            variant="outlined"
            onClick={handleOpenCodeModal}
            sx={{ textTransform: 'capitalize' }}
          >
            <CodeIcon sx={{ mr: 1 }} /> Edit HTML Code
          </Button>
        </div>

        <div className='page-top-formview mb-3'>
          <Form onSubmit={handleSubmit}>
            {/* Form Fields */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="templateName" className="mb-3">
                  <Form.Label style={{ fontWeight: 500, color: '#333' }}>Name</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={templateData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter template name"
                    style={{ 
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="product" className="mb-3">
                  <Form.Label style={{ fontWeight: 500, color: '#333' }}>Product</Form.Label>
                  <Form.Select
                    required
                    value={templateData.productId}
                    onChange={(e) => handleInputChange('productId', e.target.value)}
                    style={{ 
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Any</option>
                    {dropdownOptions.products.map((product) => (
                      <option key={product._id} value={product._id}>
                        {product.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="emailSubject" className="mb-3">
                  <Form.Label style={{ fontWeight: 500, color: '#333' }}>Subject</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={templateData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Enter email subject"
                    style={{ 
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      fontSize: '14px'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Edit Button - Outside the preview box */}
            {templateData.elements.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleOpenCodeModal}
                  sx={{
                    backgroundColor: '#667eea',
                    color: 'white',
                    textTransform: 'capitalize',
                    '&:hover': {
                      backgroundColor: '#5568d3'
                    }
                  }}
                >
                  <CodeIcon sx={{ mr: 1 }} /> Edit HTML Code
                </Button>
              </Box>
            )}

            {/* Email Preview - Show rendered HTML content inline */}
            <Box
              sx={{
                backgroundColor: '#f4f4f4',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                minHeight: '500px',
                mb: 3,
                p: 4
              }}
            >
              {templateData.elements.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    color: '#666',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    Start building your email template
                  </Typography>
                  <Typography variant="body2">
                    Click "Edit HTML Code" button above to add content
                  </Typography>
                </Box>
              ) : (
                // Render complete HTML output directly
                <Box
                  sx={{
                    backgroundColor: 'white',
                    p: 4,
                    borderRadius: '8px'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: templateData.elements
                      .map(element => {
                        if (element.type === 'text' && element.content) return element.content;
                        if (element.type === 'button') return `<div style="text-align: center; margin: 24px 0;"><a href="${element.url || '#'}" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color:white;; padding: 12px 24px; text-decoration: none; border-radius: 4px; display: inline-block; font-family: Arial, sans-serif; font-weight: 500;">${element.text || 'Click Here'}</a></div>`;
                        if (element.type === 'divider') return `<hr style="height: 2px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; margin: 24px 0; border-radius: 1px;" />`;
                        if (element.type === 'image' && element.src) return `<div style="text-align: center; margin: 24px 0;"><img src="${element.src}" alt="${element.alt || ''}" style="max-width: 100%; height: auto; border-radius: 4px;" /></div>`;
                        if (element.type === 'quote' && element.content) return `<blockquote style="border-left: 4px solid #667eea; padding-left: 16px; background-color: #f8f9ff; padding: 16px; margin: 24px 0; border-radius: 0 4px 4px 0; font-style: italic; font-family: Arial, sans-serif; color: #555;">"${element.content}"</blockquote>`;
                        return '';
                      })
                      .join('')
                  }}
                />
              )}
            </Box>


            {/* Action Buttons */}
            <Row>
              <Col md={12}>
                <Stack spacing={2} direction="row">
                  <Button 
                    variant="contained" 
                    type="submit" 
                    disabled={loading}
                    sx={{
                      backgroundColor: '#dc2626',
                      color: 'white',
                      textTransform: 'capitalize',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: '#b91c1c'
                      }
                    }}
                  >
                    {loading ? 'Updating...' : 'Update Template'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/emailtemplates')}
                    sx={{
                      borderColor: '#dc2626',
                      color: '#dc2626',
                      textTransform: 'capitalize',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        borderColor: '#b91c1c',
                        backgroundColor: 'rgba(220, 38, 38, 0.04)'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Col>
            </Row>
          </Form> 
        </div>
      </Box>
    </Box>
  );
}